/**
 * The GameAudio the game talks to where there is WebAudio. The AudioContext is not made until
 * the first gesture (`unlock`), which keeps browsers from complaining and means nothing can pile
 * up in a suspended context: until then effects are dropped, and the music asked for is
 * remembered and started on the first key. Music files, if any, play through audio elements and
 * need no context at all.
 */

import type { SimEvent } from '../sim/types';
import { Engine } from './engine';
import { FileMusic } from './fileMusic';
import { trackNames } from './trackNames';
import {
  type Ambience, type Cue, DEFAULT_VOLUMES, type GameAudio, type MusicTrack, type MusicVariant, playsOnce,
  sliderGain, type UiSound, type Volumes,
} from './types';

/** Files are finished recordings, louder than the synth: they sit a little lower. */
const FILE_TRIM = 0.7;

interface Wanted {
  readonly track: MusicTrack;
  readonly variant: MusicVariant | undefined;
}

function clampVolumes(v: Volumes): Volumes {
  const c = (x: number): number => (Number.isFinite(x) ? Math.min(1, Math.max(0, x)) : 0);
  return { master: c(v.master), sfx: c(v.sfx), music: c(v.music) };
}

export class LiveAudio implements GameAudio {
  private engine: Engine | null = null;
  /** The context could not be made: stay silent rather than try on every key. */
  private failed = false;
  private readonly files = new FileMusic();
  private volumes: Volumes = { ...DEFAULT_VOLUMES };
  private wanted: Wanted | null = null;
  /** Whether `wanted` is being played from a file (true) or the synth (false). */
  private fromFile = false;

  constructor(private readonly Ctor: typeof AudioContext) {
    this.applyFileVolume();
  }

  unlock(): void {
    if (!this.engine && !this.failed) {
      try {
        this.engine = new Engine(this.Ctor, this.volumes);
      } catch {
        this.failed = true;
      }
      const w = this.wanted;
      if (this.engine && w && !this.fromFile) this.engine.playSong(w.track, w.variant);
    }
    this.engine?.resume();
    this.files.unlock();
  }

  events(events: readonly SimEvent[], listenerX: number): void {
    this.engine?.events(events, listenerX);
  }

  ambience(a: Ambience): void {
    this.engine?.ambience(a);
  }

  ui(s: UiSound): void {
    this.engine?.ui(s);
  }

  cue(c: Cue, n?: number): void {
    this.engine?.cue(c, n ?? 0);
  }

  /**
   * Asking for what is already playing changes nothing (so it is safe to call every frame); a
   * once-only track that has finished stays finished until something else is asked for.
   */
  music(track: MusicTrack | null, variant?: MusicVariant): void {
    const w = this.wanted;
    if (track === null ? w === null : w !== null && w.track === track && w.variant === variant) return;
    if (track === null) {
      this.wanted = null;
      this.files.stop();
      this.engine?.stopSong();
      return;
    }
    this.wanted = { track, variant };
    this.fromFile = this.files.play(trackNames(track, variant), playsOnce(track));
    if (this.fromFile) this.engine?.stopSong();
    else this.engine?.playSong(track, variant);
  }

  setVolumes(v: Volumes): void {
    this.volumes = clampVolumes(v);
    this.engine?.setVolumes(this.volumes);
    this.applyFileVolume();
  }

  tracks(): string[] {
    return this.files.names();
  }

  private applyFileVolume(): void {
    this.files.setVolume(sliderGain(this.volumes.master) * sliderGain(this.volumes.music) * FILE_TRIM);
  }
}
